import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, NotebookPen } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow, PrimaryButton } from "@/components/fionova/Primitives";
import { listJournal, addJournal, shareJournalEntry } from "@/services/unsaidService";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export default function JournalPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    listJournal().then((e) => { setEntries(e); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      const entry = await addJournal(text);
      setEntries((prev) => [entry, ...prev]);
      setDraft("");
    } catch {}
    setSaving(false);
  };

  const toggleShare = async (e) => {
    const shared = !e.shared;
    setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, shared } : x)));
    try { await shareJournalEntry(e.id, shared); } catch { setEntries((prev) => prev.map((x) => (x.id === e.id ? { ...x, shared: !shared } : x))); }
  };

  return (
    <div data-testid="page-journal" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex min-h-screen w-full max-w-[600px] flex-col px-6 py-8 sm:px-8">
        <div className="fio-appbar sticky top-3 z-20 flex items-center justify-between rounded-full py-1.5 pl-4 pr-1.5" data-testid="app-header">
          <button data-testid="journal-back" onClick={() => navigate("/app")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Home
          </button>
          <Logo size={36} />
        </div>

        <div className="mt-8">
          <div className="flex items-center gap-2 text-primary">
            <NotebookPen className="h-4 w-4" />
            <Eyebrow>Daily Journal</Eyebrow>
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            If something meaningful stood out to you today, write it down.
          </h1>
        </div>

        <div className="mt-6 rounded-3xl bg-card p-3 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
          <textarea data-testid="journal-input" rows={4} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Your wins, your losses, anything on your mind…"
            className="w-full resize-none bg-transparent px-3 py-2 text-base leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none" />
        </div>
        <div className="mt-3">
          <PrimaryButton data-testid="journal-save" onClick={save} disabled={!draft.trim() || saving}>
            {saving ? "Saving…" : "Save entry"}
          </PrimaryButton>
        </div>

        <p className="eyebrow mt-10 text-[11px] text-muted-foreground">Your entries</p>
        {loaded && entries.length === 0 && (
          <div data-testid="journal-empty" className="mt-3 rounded-3xl bg-card p-6 text-center ring-1 ring-border">
            <p className="text-base leading-relaxed text-muted-foreground">
              Nothing here yet. Your first reflection will live here. Entries stay private unless you choose to share them with Ellie.
            </p>
          </div>
        )}
        <div className="mt-3 space-y-3 pb-10">
          <AnimatePresence initial={false}>
            {entries.map((e) => (
              <motion.div key={e.id} data-testid="journal-entry"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
                className="rounded-3xl bg-card p-5 ring-1 ring-border">
                <p className="eyebrow text-[11px] text-muted-foreground">{fmt(e.created_at)}</p>
                <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed text-foreground/90">{e.text}</p>
                <button data-testid={`journal-share-${e.id}`} aria-pressed={!!e.shared} onClick={() => toggleShare(e)}
                  className={`mt-3 rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors ${e.shared ? "bg-primary/10 text-primary ring-primary/40" : "text-muted-foreground ring-border hover:text-foreground"}`}>
                  {e.shared ? "Shared with Ellie · tap to stop sharing" : "Share this entry with Ellie"}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
